import {
  createNestablePublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
  type IPublicClientApplication,
} from "@azure/msal-browser";
import { config } from "../config";

/**
 * Autenticazione unica per tutta l'app.
 *
 * Dentro Teams (e Outlook / app Microsoft 365) usa Nested App Authentication:
 * il token è ottenuto tramite l'host, con SSO silenzioso.
 * Nel browser MSAL ripiega sul flusso standard: il primo accesso avviene con
 * un popup aperto da un clic dell'utente (vedi signIn), altrimenti il browser
 * lo bloccherebbe.
 *
 * Registrazione Entra ID richiesta (piattaforma SPA):
 *   - brk-multihub://<dominio-app>     (NAA)
 *   - https://<dominio-app>/blank.html (popup nel browser)
 */

/** Serve un'interazione dell'utente (primo accesso nel browser). */
export class LoginRequiredError extends Error {
  constructor() {
    super("Accesso richiesto");
    this.name = "LoginRequiredError";
  }
}

let pcaPromise: Promise<IPublicClientApplication> | undefined;
let loginHint: string | undefined;
let inHost = false;

export function initAuth(hint?: string, hosted = false): Promise<IPublicClientApplication> {
  loginHint = hint;
  inHost = hosted;
  pcaPromise ??= createNestablePublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      redirectUri: `${window.location.origin}/blank.html`,
    },
    cache: { cacheLocation: "localStorage" },
  });
  return pcaPromise;
}

async function pca(): Promise<IPublicClientApplication> {
  if (!pcaPromise) throw new Error("initAuth() non è stato chiamato");
  return pcaPromise;
}

function pickAccount(app: IPublicClientApplication): AccountInfo | undefined {
  const active = app.getActiveAccount();
  if (active) return active;
  const all = app.getAllAccounts();
  const match = loginHint ? all.find((a) => a.username.toLowerCase() === loginHint!.toLowerCase()) : undefined;
  return match ?? all[0];
}

/** true se c'è già un account utilizzabile (o se siamo dentro Teams, dove l'accesso è automatico). */
export async function hasAccount(): Promise<boolean> {
  if (inHost) return true;
  return !!pickAccount(await pca());
}

/**
 * Primo accesso nel browser. Va chiamata DIRETTAMENTE dal gestore di un clic,
 * così il popup non viene bloccato.
 */
export async function signIn(): Promise<void> {
  const app = await pca();
  const result = await app.loginPopup({ scopes: config.graphScopes, loginHint, prompt: "select_account" });
  app.setActiveAccount(result.account);
}

/* Le richieste partono in parallelo: una sola acquisizione per insieme di scope,
   e al massimo un'interazione alla volta (evita "interaction_in_progress"). */
const inFlight = new Map<string, Promise<string>>();
let interaction: Promise<unknown> | undefined;

export function getAccessToken(scopes: string[]): Promise<string> {
  const key = [...scopes].sort().join(" ");
  let p = inFlight.get(key);
  if (!p) {
    p = acquire(scopes).finally(() => inFlight.delete(key));
    inFlight.set(key, p);
  }
  return p;
}

async function acquire(scopes: string[]): Promise<string> {
  const app = await pca();
  if (interaction) await interaction.catch(() => undefined);

  const account = pickAccount(app);
  try {
    if (account) {
      const r = await app.acquireTokenSilent({ scopes, account });
      return r.accessToken;
    }
    if (inHost) {
      // Dentro Teams il token silenzioso passa dall'host anche senza account in cache
      const r = await app.ssoSilent({ scopes, loginHint });
      app.setActiveAccount(r.account);
      return r.accessToken;
    }
  } catch (e) {
    if (!(e instanceof InteractionRequiredAuthError)) throw e;
    if (!inHost && !account) throw new LoginRequiredError();
  }

  if (!inHost && !account) throw new LoginRequiredError();

  // Serve interazione (consenso, MFA, sessione scaduta): un solo popup per volta
  interaction ??= app
    .acquireTokenPopup({ scopes, loginHint, account })
    .then((r) => {
      app.setActiveAccount(r.account);
      return r;
    })
    .finally(() => {
      interaction = undefined;
    });
  await interaction;
  // Il popup può essere stato aperto da un'altra richiesta con scope diversi:
  // ora l'account è in cache, il token si ottiene in silenzio.
  return (await app.acquireTokenSilent({ scopes, account: pickAccount(app)! })).accessToken;
}

/** Token provider per Microsoft Graph, da passare a GraphClient. */
export const graphToken = () => (__DEMO__ ? Promise.resolve("demo") : getAccessToken(config.graphScopes));

/** Token provider per il backend (Worker), se configurato. */
export const apiToken = () => {
  if (!config.apiScope) throw new Error("VITE_API_SCOPE non configurato");
  return getAccessToken([config.apiScope]);
};
